/**
 * The panes of `/app/docs/:id` (MAR-1097, decided by Samuel 2026-10-20).
 *
 * Each pane — Archivo (`file`), Transcripción (`ocr`), Metadatos (`metadata`), Revisión (`review`) — is a
 * toggle. One shown takes the full width; two sit side by side at equal width, in the order they were
 * OPENED (the list is that order). Turning on a third closes the one opened longest ago. At least one
 * is always shown. A narrow screen shows one. There is no resizing.
 *
 * The state is the URL, `?panes=ocr,file`, so a view is linkable and survives a reload. Links still
 * carry the older single `?view=` (sheets, chat, the review pages), so it is read when `panes` is
 * absent and mapped onto the same set.
 */
export const DOC_PANES = ['file', 'ocr', 'metadata', 'review'] as const
export type DocPane = (typeof DOC_PANES)[number]

const isPane = (v: string): v is DocPane => (DOC_PANES as readonly string[]).includes(v)

const LEGACY_VIEW: Record<string, DocPane[]> = {
  file: ['file'],
  split: ['file', 'ocr'],
  ocr: ['ocr'],
  metadata: ['metadata'],
}

export function parsePanes(query: Record<string, unknown>): DocPane[] {
  if (typeof query.panes === 'string') {
    return [...new Set(query.panes.split(',').map(s => s.trim()).filter(isPane))]
  }
  const view = typeof query.view === 'string' ? query.view : ''
  return [...(LEGACY_VIEW[view] ?? LEGACY_VIEW.file!)]
}

/**
 * What is actually shown: the requested panes this document offers, the last `max` of them. Never
 * nothing — an identity document has its data under Metadatos, anything else starts at Archivo.
 */
export function resolvePanes(
  requested: DocPane[],
  { offered, max, isIdentityDoc }: { offered: Record<DocPane, boolean>; max: number; isIdentityDoc: boolean },
): DocPane[] {
  const kept = requested.filter(p => offered[p]).slice(-max)
  if (kept.length) return kept
  return [isIdentityDoc ? 'metadata' : 'file']
}

/** On closes it (unless it is the last one); off opens it, dropping the one opened longest ago. */
export function togglePane(current: DocPane[], pane: DocPane, max: number): DocPane[] {
  if (current.includes(pane)) return current.length > 1 ? current.filter(p => p !== pane) : current
  return [...current, pane].slice(-max)
}

/**
 * Opens `pane` if it is not shown; never closes it. What "show me the file" means. `keep` is the pane
 * the request came from (a finding in Revisión): when room is needed, another pane makes it, unless
 * there is room for only one, and then the pane asked for wins.
 */
export function showPane(current: DocPane[], pane: DocPane, max: number, keep?: DocPane): DocPane[] {
  if (current.includes(pane)) return current
  const next = [...current, pane]
  while (next.length > max) {
    const drop = next.findIndex(p => p !== pane && (p !== keep || max === 1))
    next.splice(drop, 1)
  }
  return next
}

/** The route query for `next`: `?panes=`, the old `?view=` dropped, the default (Archivo) left clean. */
export function panesQuery(query: Record<string, unknown>, next: DocPane[]): Record<string, unknown> {
  const { panes: _panes, view: _view, ...rest } = query
  return next.length === 1 && next[0] === 'file' ? rest : { ...rest, panes: next.join(',') }
}

/**
 * Where a contract review is read (MAR-1097 5/5): its document, with Archivo and Revisión open on
 * that run. Every link to a review goes here — the case card, the activity bell, the old addresses.
 */
export function documentReviewPath({ itemId, reviewId, caseId }: { itemId: string; reviewId: string; caseId?: string | null }): string {
  const query = new URLSearchParams({ panes: 'file,review', review: reviewId })
  if (caseId) query.set('caseId', caseId)
  return `/app/docs/${itemId}?${query}`
}
